/**
 * Parlay Insights Panel
 * Dynamic insights for parlay-level analytics
 * Shows rolling windows, streaks, and best performers
 */
import { formatPercent } from '@parlay-picker/shared/formatting';
import type { ReactElement } from 'react';
import type { ParlayInsights } from '../hooks/useLabDashboard';

interface ParlayInsightsPanelProps {
  data: ParlayInsights;
  loading: boolean;
  recentDays: number;
  setRecentDays: (days: number) => void;
}

function formatUnits(units: number): string {
  const sign = units >= 0 ? '+' : '';
  return `${sign}${units.toFixed(2)}u`;
}

function getWinRateColor(rate: number): string {
  if (rate >= 0.5) return 'text-theme-success';
  if (rate >= 0.4) return 'text-theme-warning';
  return 'text-theme-danger';
}

function getUnitsColor(units: number): string {
  if (units > 0) return 'text-theme-success';
  if (units === 0) return 'text-theme-text-secondary';
  return 'text-theme-danger';
}

export function ParlayInsightsPanel({
  data,
  loading,
  recentDays,
  setRecentDays,
}: ParlayInsightsPanelProps): ReactElement {
  if (loading) {
    return (
      <div className="bg-theme-surface/50 rounded-lg p-4">
        <div className="animate-pulse">
          <div className="h-4 bg-theme-surface-hover rounded w-32 mb-4" />
          <div className="grid grid-cols-2 @3xl:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-20 bg-theme-surface-hover rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const { rollingWindows, streakInfo, bestLegCount, bestTier } = data;

  return (
    <div className="bg-theme-surface/50 rounded-lg p-4 space-y-4">
      {/* Header with Controls */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-theme-text-secondary">Parlay Insights</h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-theme-text-tertiary">Recent window:</span>
          <select
            value={recentDays}
            onChange={(e) => setRecentDays(parseInt(e.target.value, 10))}
            className="bg-theme-surface-hover text-theme-text-secondary text-xs rounded px-2 py-1 border border-theme-border"
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
          </select>
        </div>
      </div>

      {/* Rolling Windows */}
      <div>
        <h4 className="text-xs text-theme-text-tertiary uppercase tracking-wide mb-2">
          Performance Over Time
        </h4>
        <div className="grid grid-cols-2 @3xl:grid-cols-4 gap-3">
          {rollingWindows.map((window) => {
            const settled = window.wins + window.losses;
            return (
              <div key={window.window} className="bg-theme-surface/50 rounded-lg p-3">
                <div className="text-theme-text-secondary text-xs mb-1">
                  {window.window === 'all'
                    ? 'All Time'
                    : `Last ${window.window.replace('d', ' days')}`}
                </div>
                <div className="flex items-baseline gap-2">
                  <span className={`text-lg font-bold ${getWinRateColor(window.winRate)}`}>
                    {settled > 0 ? formatPercent(window.winRate) : '-'}
                  </span>
                  <span className={`text-sm ${getUnitsColor(window.units)}`}>
                    {formatUnits(window.units)}
                  </span>
                </div>
                <div className="text-xs text-theme-text-tertiary mt-1">
                  {window.wins}W - {window.losses}L ({window.totalParlays} total)
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Streak Information */}
      <div className="bg-theme-surface/50 rounded-lg p-4">
        <h4 className="text-xs text-theme-text-tertiary uppercase tracking-wide mb-3">
          Streak Information
        </h4>
        <div className="grid grid-cols-3 gap-4">
          {/* Current Streak */}
          <div className="text-center">
            <div className="text-xs text-theme-text-tertiary mb-1">Current</div>
            <div
              className={`text-xl font-bold ${
                streakInfo.currentStreak.type === 'win' ? 'text-theme-success' : 'text-theme-danger'
              }`}
            >
              {streakInfo.currentStreak.count > 0
                ? `${streakInfo.currentStreak.type === 'win' ? 'W' : 'L'}${streakInfo.currentStreak.count}`
                : '-'}
            </div>
          </div>

          {/* Longest Win Streak */}
          <div className="text-center">
            <div className="text-xs text-theme-text-tertiary mb-1">Best Streak</div>
            <div className="text-xl font-bold text-theme-success">
              W{streakInfo.longestWinStreak}
            </div>
          </div>

          {/* Longest Loss Streak */}
          <div className="text-center">
            <div className="text-xs text-theme-text-tertiary mb-1">Worst Streak</div>
            <div className="text-xl font-bold text-theme-danger">
              L{streakInfo.longestLossStreak}
            </div>
          </div>
        </div>
      </div>

      {/* Best Performers */}
      <div className="grid @3xl:grid-cols-2 gap-4">
        {/* Best Tier */}
        <div className="bg-theme-surface/50 rounded-lg p-3">
          <div className="text-xs text-theme-text-tertiary uppercase tracking-wide mb-2">
            Best Tier
          </div>
          {bestTier ? (
            <>
              <div className="text-lg font-bold text-white">{bestTier.tier}</div>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-sm font-medium ${getWinRateColor(bestTier.winRate)}`}>
                  {formatPercent(bestTier.winRate)}
                </span>
                <span className="text-xs text-theme-text-tertiary">
                  ({bestTier.sample} parlays)
                </span>
              </div>
            </>
          ) : (
            <div className="text-theme-text-tertiary text-sm">Not enough data</div>
          )}
        </div>

        {/* Best Leg Count */}
        <div className="bg-theme-surface/50 rounded-lg p-3">
          <div className="text-xs text-theme-text-tertiary uppercase tracking-wide mb-2">
            Best Leg Count
          </div>
          {bestLegCount ? (
            <>
              <div className="text-lg font-bold text-white">{bestLegCount.legCount} legs</div>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-sm font-medium ${getWinRateColor(bestLegCount.winRate)}`}>
                  {formatPercent(bestLegCount.winRate)}
                </span>
                <span className="text-xs text-theme-text-tertiary">
                  ({bestLegCount.sample} parlays)
                </span>
              </div>
            </>
          ) : (
            <div className="text-theme-text-tertiary text-sm">Not enough data</div>
          )}
        </div>
      </div>
    </div>
  );
}
